import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId, isIntegrationUnConnectable } from '../../../common/integrations'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { Button } from '../../../components/Button/Button'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../store/user/user'
import React, { useMemo, useState } from 'react'
import { IntegrationCardDescription } from '../../../components/Integrations/IntegrationCard/IntegrationCardDescription'
import { IntegrationConnection } from '../Connectors/IntegrationConnection'
import { IntegrationState } from '../../../components/Integrations/IntegrationState'
import { useNavigate } from 'react-router-dom'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'

export type IntegrationRowProps = {
  serviceId: IntegrationServiceId
}

export const IntegrationRow: React.FC<IntegrationRowProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isLoading: userIsLoading } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()
  const hasIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === serviceId))
  }, [userData, serviceId])

  const navigate = useNavigate()

  const currentIntegration = useMemo(() => {
    return userData?.integrations?.find((i) => (i.serviceId as IntegrationServiceId) === serviceId)
  }, [userData, serviceId])

  const uninstallIntegration = ({ serviceId }: { serviceId: IntegrationServiceId }) => {
    if (serviceId === IntegrationServiceId.ZOOM) {
      window.open('https://marketplace.zoom.us/user/installed', '_blank', 'noopener noreferrer')
      return
    }
    const user = userData
    const integration = user!.integrations!.find((i) => i.serviceId === serviceId)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: user!.id,
        integrationId: integration!.id,
      },
    })
    navigate(
      {
        pathname: `/account/integration-settings/${serviceId}/about`,
      },
      { replace: true },
    )
  }

  const [startConnect, setStartConnect] = useState<boolean>(false)

  const onConnected = () => {
    setStartConnect(false)
  }

  const disableBecauseOtherMailIntegration = useMemo(() => {
    const mailIntegrationServiceIds = getIntegrationServiceIdsForCategory(IntegrationCategory.MAIL)
    if (!mailIntegrationServiceIds.includes(serviceId)) {
      return false
    }
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.type === 'mail' && i.serviceId !== serviceId))
  }, [userData, serviceId])

  const { hasScope } = useZocksAuth()
  const hasIntegrationConnectScope = useMemo(() => {
    return hasScope('**', 'integration:connect', ['integration:admin'])
  }, [hasScope])

  const disableConnectButton = useMemo(() => {
    return disableBecauseOtherMailIntegration || !hasIntegrationConnectScope || userIsLoading
  }, [disableBecauseOtherMailIntegration, hasIntegrationConnectScope, userIsLoading])

  const hideConnectButton = useMemo(() => isIntegrationUnConnectable(serviceId), [serviceId])

  return (
    <Container fluid>
      <IntegrationConnection serviceId={serviceId} startConnect={startConnect} onConnected={onConnected} />

      <Row className='d-flex justify-content-between align-items-center'>
        <Col>
          <Stack direction='horizontal' className='gap-4 align-items-center'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100 align-self-start'>
              <IntegrationIcon serviceId={serviceId} width={48} height={48} />
            </div>
            <IntegrationCardDescription serviceId={serviceId} headerAs='h3' />
          </Stack>
        </Col>

        <Col xs='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-center'>
            <div className='d-flex align-items-center'>
              <IntegrationState currentIntegration={currentIntegration} className='d-flex flex-row align-items-center gap-1' />
            </div>

            {!hideConnectButton && (
              <div className='connect-button-container'>
                {hasIntegration ? (
                  <div className='d-flex align-items-center gap-3'>
                    {serviceId !== IntegrationServiceId.PHONE ? (
                      <Button
                        variant='primaryGhost'
                        onClick={() => uninstallIntegration({ serviceId })}
                        loading={isDeletingIntegration}
                        disabled={userIsLoading}
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <Button variant='primary' onClick={() => setStartConnect(true)} disabled={userIsLoading}>
                        Add more phone numbers
                      </Button>
                    )}
                  </div>
                ) : (
                  <ScopedBlock
                    scope={{
                      scope: 'integration:connect',
                      superScope: ['integration:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Button variant='primary' onClick={() => setStartConnect(true)} disabled={disableConnectButton}>
                      Connect
                    </Button>
                  </ScopedBlock>
                )}
              </div>
            )}
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
